import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, ButtonBase, Card, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import { status as statusTokens } from '../../theme';
import { fill, Series } from '../payroll/reportCharts';
import { InsightPerson } from '../../api/services/hiring.service';

// The insights page's charts. Same rules as the payroll reports: one
// colour per meaning, thin marks, a 2px gap between segments, round only
// at the data end, values in text ink, a tooltip on every bar.
//
// Every row here is a horizontal bar because every question here is "how
// many, per named thing" — stages, steps, sources — and the names are too
// long to sit under columns. Rows that lead somewhere are buttons.

/** A segment's colour: a series, or the warning status for what is late. */
export type Tone = Series | 'warn';

const toneSx = (tone: Tone) => (tone === 'warn'
  ? [{ bgcolor: statusTokens.warn }]
  : fill(tone));

export const ToneSwatch: React.FC<{ tone: Tone }> = ({ tone }) => (
  <Box component="span" sx={[{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', mr: 0.5, flexShrink: 0 }, ...toneSx(tone)]} />
);

export const ToneLegend: React.FC<{ items: { tone: Tone; label: string }[] }> = ({ items }) => (
  <Stack direction="row" gap={1.25} flexWrap="wrap" component="span">
    {items.map((i) => (
      <Typography key={i.label} variant="caption" color="text.secondary" component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <ToneSwatch tone={i.tone} />{i.label}
      </Typography>
    ))}
  </Stack>
);

export interface BarSegment { key: string; label: string; value: number; tone: Tone }
export interface BarRow {
  key: string;
  label: string;
  segments: BarSegment[];
  /** The number at the end of the bar. Defaults to the segments' sum. */
  value?: React.ReactNode;
  /** A short note after the value — a rate, an age. */
  note?: React.ReactNode;
  /** Drawn faded: a row that is there for the order, not for its number. */
  muted?: boolean;
}

/**
 * Rows of horizontal bars on one shared scale. A segment wide enough for
 * its count says it inside the bar, in text ink on the surface colour
 * behind it; a narrow one leaves it to the tooltip and the row's total.
 */
export const BarRows: React.FC<{
  rows: BarRow[];
  /** The scale's top. Defaults to the largest row. */
  max?: number;
  labelWidth?: number;
  /** Count inside wide segments — for palettes under 3:1. */
  inlineCounts?: boolean;
  selected?: string | null;
  onPick?: (key: string) => void;
}> = ({ rows, max: maxAsked, labelWidth = 130, inlineCounts, selected, onPick }) => {
  const total = (r: BarRow) => r.segments.reduce((n, s) => n + s.value, 0);
  const max = maxAsked || Math.max(1, ...rows.map(total));
  return (
    <Stack gap={0.5}>
      {rows.map((row) => {
        const sum = total(row);
        const tip = (
          <Box sx={{ py: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{row.label}</Typography>
            {row.segments.filter((s) => s.value > 0).map((s) => (
              <Stack key={s.key} direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}><ToneSwatch tone={s.tone} />{s.label}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{s.value}</Typography>
              </Stack>
            ))}
            {sum === 0 && <Typography variant="caption">Nobody</Typography>}
          </Box>
        );
        const picked = selected === row.key;
        const body = (
          <Box
            sx={{
              display: 'grid',
              // Narrower label and note on a phone, so the bar keeps some room.
              gridTemplateColumns: {
                xs: '84px minmax(24px, 1fr) 32px minmax(0, 76px)',
                sm: `${labelWidth}px minmax(40px, 1fr) 40px minmax(64px, auto)`,
              },
              alignItems: 'center',
              columnGap: 1.5,
              width: '100%',
              py: 0.75,
              px: 1,
              mx: -1,
              borderRadius: 1,
              opacity: row.muted ? 0.6 : 1,
              bgcolor: picked ? 'action.selected' : 'transparent',
              textAlign: 'left',
            }}
          >
            <Typography variant="body2" noWrap title={row.label}>{row.label}</Typography>
            <Box sx={{ display: 'flex', gap: '2px', height: 14, minWidth: 0 }}>
              {row.segments.filter((s) => s.value > 0).map((s, i, live) => {
                const last = i === live.length - 1;
                const share = (s.value / max) * 100;
                return (
                  <Box
                    key={s.key}
                    sx={[
                      {
                        width: `${share}%`,
                        minWidth: 3,
                        height: '100%',
                        // Square on the baseline, round at the data end.
                        borderRadius: last ? '0 4px 4px 0' : 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      },
                      ...toneSx(s.tone),
                    ]}
                  >
                    {inlineCounts && share >= 7 && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10, fontWeight: 700, lineHeight: '12px', px: 0.5, borderRadius: '3px',
                          bgcolor: 'background.paper', color: 'text.primary', fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {s.value}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {row.value ?? sum}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" noWrap>{row.note ?? ''}</Typography>
          </Box>
        );
        return (
          <Tooltip key={row.key} title={tip} placement="top-start" enterDelay={150} disableInteractive>
            {onPick ? (
              <ButtonBase
                onClick={() => onPick(row.key)}
                aria-pressed={picked}
                sx={{ display: 'block', width: '100%', borderRadius: 1, '&:hover > div': { bgcolor: 'action.hover' }, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
              >
                {body}
              </ButtonBase>
            ) : <Box>{body}</Box>}
          </Tooltip>
        );
      })}
    </Stack>
  );
};

/** A chart's card: the title on the left, the legend or a short hint on the right. */
export const ChartCard: React.FC<{
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, aside, children }) => (
  <Card sx={{ p: 2.5, overflow: 'visible', minWidth: 0 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      {typeof aside === 'string'
        ? <Typography variant="caption" color="text.secondary">{aside}</Typography>
        : aside}
    </Stack>
    {children}
  </Card>
);

/** The people behind a number, each one a link to their record. */
export const PeopleChips: React.FC<{
  people: InsightPerson[];
  total?: number;
  detail?: (p: InsightPerson) => string | null;
}> = ({ people, total, detail }) => {
  const navigate = useNavigate();
  const more = (total ?? people.length) - people.length;
  return (
    <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
      {people.map((p) => {
        const extra = detail?.(p);
        return (
          <Chip
            key={p._id}
            size="small"
            variant="outlined"
            clickable
            onClick={() => navigate(`/hiring/${p._id}`)}
            label={extra ? `${p.name} · ${extra}` : p.name}
          />
        );
      })}
      {more > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          and {more} more
        </Typography>
      )}
    </Stack>
  );
};

